import { useEffect } from "react";
import { authApi } from "@/lib/api/auth";
import { useAuthStore } from "@/store/authStore";
import { endSession } from "@/lib/auth/endSession";

/**
 * Signs the user out when their refresh token was revoked remotely (e.g. from
 * another browser profile). Access JWTs can remain valid for ~60 minutes after
 * refresh revocation unless we actively check session status.
 */
export function useRemoteSessionGuard() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    let cancelled = false;

    const checkSession = async () => {
      try {
        const data = await authApi.listSessions();
        if (cancelled) return;
        if (data.current_session_active === false) {
          endSession();
        }
      } catch {
        // Ignore transient network errors; 401 refresh flow handles hard auth failures.
      }
    };

    const onVisible = () => {
      if (document.visibilityState === "visible") {
        void checkSession();
      }
    };

    void checkSession();
    window.addEventListener("focus", checkSession);
    document.addEventListener("visibilitychange", onVisible);
    const intervalId = window.setInterval(checkSession, 30_000);

    return () => {
      cancelled = true;
      window.removeEventListener("focus", checkSession);
      document.removeEventListener("visibilitychange", onVisible);
      window.clearInterval(intervalId);
    };
  }, [isAuthenticated]);
}
